import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import React, { useEffect, useRef, useState } from "react";

import { ViewWrapper } from "@components";

import { ASSISTANT_PAGE_TITLE } from "@helpers/constants";
import { ASSISTANT_EVENTS } from "@helpers/events";

import MessageFormatTest from "../components/MessageFormatTest";
import MessageList from "../components/MessageList";
import { useSharedAssistantChat } from "../context/AssistantChatContext";

const AssistantContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== "isCompactMode",
})<{ isCompactMode?: boolean }>(({ theme, isCompactMode }) => ({
  height: "100%",
  padding: isCompactMode ? theme.spacing(2) : theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isCompactMode",
})<{ isCompactMode?: boolean }>(({ theme, isCompactMode }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: isCompactMode ? theme.spacing(1) : theme.spacing(2),
}));

const InputContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCompactMode",
})<{ isCompactMode?: boolean }>(({ theme, isCompactMode }) => ({
  display: "flex",
  padding: isCompactMode ? theme.spacing(1) : theme.spacing(2),
  gap: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const AssistantHome: React.FC<{ isCompactMode?: boolean }> = ({
  isCompactMode = false,
}) => {
  const {
    messages,
    inputText,
    setInputText,
    isLoading,
    error,
    createNewSession,
    handleSendMessage,
    handleKeyPress,
    retryLastRequest,
    useComponentRefs,
  } = useSharedAssistantChat();

  const { messagesEndRef, messagesContainerRef, inputRef, handleScroll } =
    useComponentRefs("assistant-home");

  const handleSendMessageForHome = () => {
    tracker.event(ASSISTANT_EVENTS.MESSAGE_SENT);
    handleSendMessage("assistant-home");
  };
  const handleKeyPressForHome = (e: React.KeyboardEvent) => {
    handleKeyPress(e as any, "assistant-home");
  };
  const handleCreateNewSession = () => {
    tracker.event(ASSISTANT_EVENTS.NEW_CONVERSATION_STARTED);
    createNewSession("assistant-home");
  };

  const [formatTestOpen, setFormatTestOpen] = useState<boolean>(false);

  const openFormatTest = () => setFormatTestOpen(true);
  const closeFormatTest = () => setFormatTestOpen(false);

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && messages.length === 0) {
      hasInitialized.current = true;
      createNewSession("assistant-home");
    }
    // eslint-disable-next-line
  }, []); // On initial mount, start new session

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  return (
    <ViewWrapper title={ASSISTANT_PAGE_TITLE} docsSection="ASSISTANT">
      <AssistantContainer
        maxWidth={false}
        disableGutters
        isCompactMode={isCompactMode}
      >
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Box display="flex" gap={1}>
            {isLocalhost && (
              // This is just for debugging purposes when running locally
              <Tooltip title="Test message formatting">
                <Button
                  onClick={openFormatTest}
                  variant="outlined"
                  color="secondary"
                  size="medium"
                >
                  Format Test
                </Button>
              </Tooltip>
            )}
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleCreateNewSession}
              variant="outlined"
              color="primary"
            >
              New Conversation
            </Button>
          </Box>
        </Box>
        <ChatContainer isCompactMode={isCompactMode} elevation={3}>
          <MessageList
            messages={messages}
            isLoading={isLoading}
            messagesEndRef={messagesEndRef}
            messagesContainerRef={messagesContainerRef}
            handleScroll={handleScroll}
            retryLastRequest={retryLastRequest}
            isCompactMode={isCompactMode}
            role="region"
          />
          <InputContainer isCompactMode={isCompactMode}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              variant="outlined"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPressForHome}
              disabled={isLoading}
              multiline
              maxRows={4}
              inputRef={inputRef}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessageForHome}
              disabled={!inputText.trim() || isLoading}
            >
              <SendIcon />
            </IconButton>
          </InputContainer>
        </ChatContainer>

        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity="error">{error}</Alert>
          </Snackbar>
        )}
        <MessageFormatTest open={formatTestOpen} onClose={closeFormatTest} />
      </AssistantContainer>
    </ViewWrapper>
  );
};

export default AssistantHome;
